import { Box } from '@mui/material';
import { cloneElement } from 'react';
import Dropzone, { ErrorCode, FileRejection } from 'react-dropzone';

import { useAlert } from 'hooks';
import { UploadFile } from 'types';

import { DropZoneProps } from './types';

const DropZone = ({
  accept,
  setValue,
  children,
  disabled,
  maxFiles,
  className,
  errorMessage,
  noClick = false,
  multiple = false,
  ...props
}: DropZoneProps) => {
  const { setIsError, setIsLoading, allowedFileSize = 5 } = props;
  const allowedSize = allowedFileSize * 1024 * 1024;

  const { showErrorAlert } = useAlert();

  const handleDrop = async (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[]
  ) => {
    if (acceptedFiles.length > 0) {
      if (setIsLoading) setIsLoading(true);

      const values = await Promise.all(
        acceptedFiles.map((item) => {
          const reader = new FileReader();

          return new Promise((resolve) => {
            reader.onload = () => {
              resolve({
                file: item,
                src: reader.result,
              });
            };
            reader.readAsDataURL(item);
          });
        })
      );
      if (setIsError) setIsError(false);
      if (setIsLoading) setIsLoading(false);

      if (multiple) {
        setValue(values as UploadFile[]);
      } else {
        setValue(values[0] as UploadFile);
      }
    }

    if (rejectedFiles.length > 0) {
      if (setIsError) setIsError(true);

      if (errorMessage) {
        const errorCode = rejectedFiles[0].errors[0].code;

        if (errorCode === ErrorCode.FileTooLarge) {
          showErrorAlert(`Only files less then ${allowedFileSize}MB allowed.`);
        } else if (errorCode === ErrorCode.FileInvalidType && accept) {
          showErrorAlert(
            `File type must be ${Object.keys(accept).join(', ')}.`
          );
        }
      }
    }
  };

  return (
    <Dropzone
      accept={accept}
      noClick={noClick}
      multiple={multiple}
      maxFiles={maxFiles}
      disabled={disabled}
      onDrop={handleDrop}
      test-id="drop-zone"
      maxSize={allowedSize}
    >
      {({ getRootProps, getInputProps }) => (
        <Box
          {...getRootProps()}
          className={className}
          sx={{
            width: '100%',
            cursor: 'pointer',
            '&:focus-visible': {
              outline: 'unset',
            },
          }}
        >
          <input {...getInputProps()} />

          {cloneElement(children, { ...props })}
        </Box>
      )}
    </Dropzone>
  );
};

export { DropZone };
export type { DropZoneProps };
